// ============================================================
// Datei: src/components/guestList/GuestListMobile.jsx
//
// FINAL:
// - gleiche Filterlogik wie Desktop
// - sichere Anzeige (ROLE + ID basiert)
// - Buttons funktionieren weiterhin
// - NEU: Default-Sortierung nach guestId DESC
//        => zuletzt hinzugefügter Gast steht zuoberst
// ============================================================

import React, { useMemo, useState } from "react";
import "./guestListMobile.css";

const GuestListMobile = ({
    guests = [],
    onEdit = null,
    onDelete = null
}) => {

    // ============================================================
    // USER INFOS (gleich wie Desktop)
    // ============================================================
    const role = localStorage.getItem("role");
    const department = localStorage.getItem("department");
    const volunteerId = localStorage.getItem("volunteerId");
    const username = localStorage.getItem("username");

    // ============================================================
    // DEFAULT SORT CONFIG
    // WICHTIG:
    // Mobile soll standardmässig gleich sortieren wie Desktop:
    // höchste guestId zuerst = neuester Gast oben
    // ============================================================
    const [sortConfig] = useState({
        key: "guestId",
        direction: "desc"
    });

    // ============================================================
    // ZENTRALE SORTIERFUNKTION
    // Damit bleibt die Logik sauber und erweiterbar.
    // ============================================================
    const sortGuests = (guestList, config) => {
        if (!Array.isArray(guestList)) return [];

        const { key, direction } = config || {};

        if (!key || !direction) {
            return [...guestList];
        }

        return [...guestList].sort((a, b) => {
            const aVal = a?.[key];
            const bVal = b?.[key];

            // --------------------------------------------------------
            // null / undefined defensiv behandeln
            // --------------------------------------------------------
            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return direction === "asc" ? -1 : 1;
            if (bVal == null) return direction === "asc" ? 1 : -1;

            // --------------------------------------------------------
            // Zahlenvergleich, wichtig für guestId
            // --------------------------------------------------------
            const aNum = Number(aVal);
            const bNum = Number(bVal);
            const bothNumeric = !Number.isNaN(aNum) && !Number.isNaN(bNum);

            if (bothNumeric) {
                return direction === "asc"
                    ? aNum - bNum
                    : bNum - aNum;
            }

            // --------------------------------------------------------
            // Fallback: Stringvergleich
            // --------------------------------------------------------
            const aStr = String(aVal).toLowerCase();
            const bStr = String(bVal).toLowerCase();

            const compareResult = aStr.localeCompare(bStr, "de", {
                sensitivity: "base"
            });

            return direction === "asc" ? compareResult : -compareResult;
        });
    };

    // ============================================================
    // FILTER LOGIK (IDENTISCH MIT DESKTOP)
    // Danach IMMER default nach guestId DESC sortieren
    // ============================================================
    const filteredGuests = useMemo(() => {
        let visibleGuests = [];

        // ADMIN sieht alles
        if (role === "ADMIN") {
            visibleGuests = guests;
        }
        // OK von KASSE sieht alles
        else if (role === "OK" && department?.toLowerCase() === "kasse") {
            visibleGuests = guests;
        }
        // OK → nur eigene Gäste
        else if (role === "OK") {
            visibleGuests = guests.filter((g) => {

                // BEST CASE → ID vergleichen
                if (g.recordedById && volunteerId) {
                    return String(g.recordedById) === String(volunteerId);
                }

                // FALLBACK → Name vergleichen
                return g.recordedByFullName
                    ?.toLowerCase()
                    .includes(username?.toLowerCase());
            });
        }

        // Default-Sortierung: neuester oben
        return sortGuests(visibleGuests, sortConfig);

    }, [guests, role, department, volunteerId, username, sortConfig]);

    // ============================================================
    // RENDER
    // ============================================================
    return (
        <div className="guest-list-mobile-container">
            <h3 className="guest-list-title">Gästeliste</h3>

            {filteredGuests.length === 0 && (
                <p className="guest-list-empty">Keine Gäste sichtbar.</p>
            )}

            {filteredGuests.map((g) => (
                <div key={g.guestId} className="guest-card">

                    <div className="guest-card-row">
                        <strong>{g.firstName} {g.lastName}</strong>
                    </div>

                    <div className="guest-card-row">
                        Eintritt: {g.admission}
                    </div>

                    <div className="guest-card-row">
                        Rolle: {g.roleName}
                    </div>

                    <div className="guest-card-row">
                        Erfasst von: {g.recordedByFullName}
                    </div>

                    <div className="guest-card-actions">
                        <button
                            className="btn-edit"
                            disabled={!onEdit}
                            onClick={() => onEdit && onEdit(g)}
                        >
                            ✏️ Bearbeiten
                        </button>

                        <button
                            className="btn-delete"
                            disabled={!onDelete}
                            onClick={() => onDelete && onDelete(g)}
                        >
                            🗑️ Löschen
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default GuestListMobile;